import React, { useState, useEffect } from 'react';

import { getDocument } from '../../DiscourseIndex/components/restApi';
import {
  DocumentManager,
  setCursorPositionInDOM,
  handleSelectionChange,
  getDescendantNodeAndOffset,
} from '../../RichTextEditor';

import DiscourseLoading from './DiscourseLoading';
import DiscoursePublishingWorkflowControls from './DiscoursePublishingWorkflowControls';
import DiscoursePublishingInfoSection from './DiscoursePublishingInfoSection';
import DiscourseFormattingControls from './DiscourseFormattingControls';
import DiscourseMainSection from './DiscourseMainSection';
import DiscourseDebugger from './DiscourseDebugger';
import Feedback from '../../Shared/UI/Feedback/components/Feedback';

import styles from './Discourse.module.scss';

const Discourse = ({
  authenticatedUser,
  documentManagerId,
  id,
  openWithContentEditable
}) => {
  const [state, setState] = useState({
    documentManager: new DocumentManager({ id: documentManagerId }),
    loading: !!id,
    feedbackMessage: null,
    feedbackVariant: null,
  });
  const [contentEditable, setContentEditable] = useState(openWithContentEditable);

  const clearFeedback = () => {
    setState((prevState) => {
      const nextState = {
        ...prevState,
        feedbackMessage: null,
        feedbackVariant: null,
      };

      return nextState;
    });
  };

  const onResponse = (response) => {
    setState(prevState => {
      const nextState = { ...prevState, loading: response.loading };
      nextState.documentManager.addDocumentToHistoryFromData(response);

      return nextState;
    });
  };

  const onError = feedback => setState(prevState => ({ ...prevState, ...feedback }));

  useEffect(() => {
    if (id) {
      getDocument({
        documentId: id,
        onResponse,
        onError,
      });
    }
  }, []);

  useEffect(() => {
    setCursorPositionInDOM({
      editableContentId: styles.mainSection,
      isibuteDocument: state.documentManager.getCurrentDocument(),
    });
  }, [state.documentManager.historyIndex]);

  useEffect(() => {
    if (contentEditable) {
      const editableElement = document.getElementById(styles.mainSection);
      if (editableElement) {
        editableElement.focus();

        const { descendantNode, offset } = getDescendantNodeAndOffset({
          node: editableElement,
          targetPosition: 1,
        });

        const selection = window.getSelection();
        const range = document.createRange();
        range.setStart(descendantNode, offset);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        handleSelectionChange({ editableContentId: styles.mainSection, setState });
      }
    }
  }, [contentEditable]);

  if (state.loading) {
    return <DiscourseLoading />;
  }

  return (
    <>
      <DiscoursePublishingWorkflowControls
        authenticatedUser={authenticatedUser}
        contentEditable={contentEditable}
        setContentEditable={setContentEditable}
        state={state}
        setState={setState}
      />
      <DiscoursePublishingInfoSection document={state.documentManager.getCurrentDocument()} />
      <DiscourseFormattingControls
        contentEditable={contentEditable}
        setState={setState}
        activeStyleTypes={state.activeStyleTypes}
        activeFormat={state.activeFormat}
        activeHref={state.activeHref}
        editableContentId={styles.mainSection}
      />
      <DiscourseMainSection
        authenticatedUser={authenticatedUser}
        setState={setState}
        state={state}
        contentEditable={contentEditable}
      />
      <DiscourseDebugger state={state} />
      <Feedback
        message={state?.feedbackMessage}
        variant={state?.feedbackVariant}
        closeFeedback={clearFeedback}
      />
    </>
  );
};

export default Discourse;
